import {
  Alert,
  Badge,
  Box,
  Button,
  CopyToClipboard,
  Header,
  KeyValuePairs,
  Modal,
  SpaceBetween,
  Spinner,
  StatusIndicator,
  Tabs,
  ToggleButton,
} from "@amzn/awsui-components-react";
import CodeView from "@amzn/awsui-code-view/code-view";
import { useMemo, useState } from "react";
import ReactJson from "react-json-view";
import { DLQItem, Stage } from "../../lusca-search-api/generated-src";
import { useFetchDlqEvent } from "../../hooks/useFetchDlqInfo";
import { useReactJsonProps } from "../../hooks/useReactJsonProps";
import { sortedStringify } from "../../helpers/jsonUtils";
import ErrorContentRenderer from "../cbe/ErrorContentRenderer";
import { ErrorDetails } from "../../types/errors";
import { CanonicalBusinessEvent } from "../../types/cbes";
import { parseErrorDetails } from "../../helpers/errorUtils";
import { formatTimestamp } from "./investigationUtils";
import { TransactionLink } from "./TransactionLink";

export interface DlqEventModalProps {
  stage: Stage;
  dlqPk: string;
  /** Shown while the Athena lookup is running / as a fallback link. */
  transactionId?: string | null;
  orderId?: string | null;
  onDismiss: () => void;
}

const bodyText = (item: DLQItem): string =>
  typeof item.processed_body === "string"
    ? item.processed_body
    : sortedStringify(item.processed_body, 2);

const CBE_DESTINATIONS = ["FLARE", "RACE"];

/**
 * For a USL publisher DLQ item (the body is a CBE with a FLARE/RACE
 * destination) parse the stack trace the same way the transaction page does,
 * so the FLARE Ion error / rACE ignored records show formatted. Null when the
 * item is not a CBE post or nothing beyond the raw trace could be extracted.
 */
export const parseCbeDlqError = (
  item: DLQItem
): { destination: string; details: ErrorDetails } | null => {
  const body = item.processed_body as Record<string, unknown> | undefined;
  const destination =
    body && typeof body === "object" && typeof body.destination === "string"
      ? body.destination.toUpperCase()
      : undefined;
  if (!destination || !CBE_DESTINATIONS.includes(destination)) return null;
  if (!item.error_stack_trace) return null;
  const details = parseErrorDetails(
    item.error_stack_trace,
    destination,
    body as unknown as CanonicalBusinessEvent
  );
  const formatted =
    destination === "FLARE"
      ? !!details.parsedErrorDetails
      : !!details.errorRecords;
  return formatted ? { destination, details } : null;
};

/**
 * Full DLQ event (input body + error) for a single DLQ PK via /dlq-info?dlqPk=.
 * Live items come straight from the DLQ table (instant); items that have
 * already been redriven fall back to the hourly Athena snapshot.
 */
export const DlqEventModal: React.FC<DlqEventModalProps> = ({
  stage,
  dlqPk,
  transactionId,
  orderId,
  onDismiss,
}) => {
  const { status, dlqItems, error } = useFetchDlqEvent(stage, dlqPk);
  const item = dlqItems?.[0];
  const [expanded, setExpanded] = useState(true);
  // null until the user picks a tab: the formatted FLARE/rACE error opens
  // first when there is one, otherwise the input event.
  const [activeTabId, setActiveTabId] = useState<string | null>(null);
  const jsonViewProps = useReactJsonProps({ sortKeys: true });

  const errorDetails: ErrorDetails | null = item
    ? {
        errorType: item.error_type,
        displayMessage: item.error_stack_trace,
        parsedErrorDetails: {
          errorType: item.error_type,
          errorMessage: item.error_message,
        },
        stackTrace: item.error_stack_trace,
      }
    : null;
  const cbeError = useMemo(
    () => (item ? parseCbeDlqError(item) : null),
    [item]
  );

  const renderBody = () => {
    if (!item) return null;
    if (item.is_malformed_json) {
      return (
        <SpaceBetween size="s">
          <Alert type="error" header="Malformed JSON">
            The message body is not valid JSON; the raw content is shown below.
          </Alert>
          <CodeView content={item.processed_body as string} wrapLines />
        </SpaceBetween>
      );
    }
    return (
      <ReactJson
        src={item.processed_body as object}
        collapsed={expanded ? false : 3}
        style={{ maxHeight: "60vh", overflow: "auto" }}
        {...jsonViewProps}
      />
    );
  };

  const renderContent = () => {
    if (status === "loading") {
      return (
        <Box textAlign="center" padding="l">
          <SpaceBetween size="xs" alignItems="center">
            <Spinner size="large" />
            <Box color="text-body-secondary">Loading the DLQ event…</Box>
          </SpaceBetween>
        </Box>
      );
    }
    if (status === "error") {
      return (
        <Alert type="error" header="Lookup failed">
          {error?.message || "The DLQ snapshot query failed."}
        </Alert>
      );
    }
    if (!item) {
      return (
        <Alert type="warning" header="Not found in the DLQ snapshot">
          No DLQ event with this PK exists in the hourly snapshot. It may have
          been redriven before the snapshot was taken, or the PK may be from a
          different stage.
        </Alert>
      );
    }
    return (
      <SpaceBetween size="m">
        <KeyValuePairs
          columns={4}
          items={[
            { label: "Queue", value: item.eventsourcekey },
            { label: "DLQ time", value: formatTimestamp(item.timestamp) },
            {
              label: "Transaction",
              value: (
                <TransactionLink
                  stage={stage}
                  transactionId={transactionId}
                  orderId={orderId}
                />
              ),
            },
            {
              label: "Live DLQ",
              value:
                status === "inDlq" ? (
                  <StatusIndicator type="warning">Still in DLQ</StatusIndicator>
                ) : (
                  <StatusIndicator type="success">
                    No longer in DLQ
                  </StatusIndicator>
                ),
            },
          ]}
        />
        <Tabs
          activeTabId={activeTabId ?? (cbeError ? "cbe-error" : "event")}
          onChange={({ detail }) => setActiveTabId(detail.activeTabId)}
          tabs={[
            {
              id: "event",
              label: (
                <SpaceBetween direction="horizontal" size="xs">
                  <span>Input event</span>
                  {item.is_malformed_json && (
                    <Badge color="red">Malformed</Badge>
                  )}
                </SpaceBetween>
              ),
              content: renderBody(),
            },
            ...(cbeError
              ? [
                  {
                    id: "cbe-error",
                    label: `${cbeError.destination} error · ${cbeError.details.errorType}`,
                    content: (
                      <SpaceBetween size="s">
                        {cbeError.details.inputRequestUrl && (
                          <Button
                            iconName="external"
                            href={cbeError.details.inputRequestUrl}
                            target="_blank"
                          >
                            Open {cbeError.destination} request
                          </Button>
                        )}
                        <ErrorContentRenderer
                          errorDetails={cbeError.details}
                          isModalVisible
                          expandAll={expanded}
                        />
                      </SpaceBetween>
                    ),
                  },
                ]
              : []),
            {
              id: "error",
              label: `${cbeError ? "Raw error" : "Error"} · ${item.error_type}`,
              content: errorDetails && (
                <ErrorContentRenderer
                  errorDetails={errorDetails}
                  isModalVisible
                  expandAll={expanded}
                />
              ),
            },
          ]}
        />
      </SpaceBetween>
    );
  };

  return (
    <Modal
      visible
      onDismiss={onDismiss}
      size="max"
      header={
        <Header
          variant="h2"
          description={<code style={{ wordBreak: "break-all" }}>{dlqPk}</code>}
        >
          DLQ event
        </Header>
      }
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            {item && (
              <>
                <CopyToClipboard
                  copyButtonText="Copy input event"
                  copySuccessText="Copied"
                  copyErrorText="Copy failed"
                  textToCopy={bodyText(item)}
                />
                <CopyToClipboard
                  copyButtonText="Copy raw error"
                  copySuccessText="Copied"
                  copyErrorText="Copy failed"
                  textToCopy={
                    item.error_stack_trace ||
                    `${item.error_type}: ${item.error_message}`
                  }
                />
                <ToggleButton
                  pressed={expanded}
                  onChange={({ detail }) => setExpanded(detail.pressed)}
                  iconName="zoom-in"
                  pressedIconName="zoom-out"
                >
                  {expanded ? "Collapse all" : "Expand all"}
                </ToggleButton>
              </>
            )}
            <Button variant="primary" onClick={onDismiss}>
              Close
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      {renderContent()}
    </Modal>
  );
};

export interface DlqPkCellProps {
  stage: Stage;
  dlqPk?: string | null;
  transactionId?: string | null;
  orderId?: string | null;
}

/**
 * A DLQ PK rendered as a link that opens the full event (input + error) in a
 * modal. Use wherever a population table shows a DLQ PK.
 */
export const DlqPkCell: React.FC<DlqPkCellProps> = ({
  stage,
  dlqPk,
  transactionId,
  orderId,
}) => {
  const [open, setOpen] = useState(false);
  if (!dlqPk) return <span>—</span>;
  return (
    <>
      <Button
        variant="inline-link"
        onClick={() => setOpen(true)}
        ariaLabel={`View DLQ event ${dlqPk}`}
      >
        <span style={{ wordBreak: "break-all" }}>{dlqPk}</span>
      </Button>
      {open && (
        <DlqEventModal
          stage={stage}
          dlqPk={dlqPk}
          transactionId={transactionId}
          orderId={orderId}
          onDismiss={() => setOpen(false)}
        />
      )}
    </>
  );
};
